import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router";
import { configureAnalytics, trackPage } from "./analytics/analytics";
import { createVisitTracker } from "./analytics/visits";
import { installChunkReload } from "./app/chunk-reload";
import { loadAppConfig } from "./app/config";
import { createAppKernel } from "./app/kernel";
import { AppProviders } from "./app/providers";
import { createQueryClient } from "./app/query-client";
import { createAppRouter } from "./app/router";
import { trackSession } from "./app/session-ready";
import "./styles.css";

// Chunk antigo após deploy / reotimização do Vite em dev: recarrega uma única vez.
installChunkReload();

async function bootstrap() {
  const root = createRoot(document.getElementById("root")!);

  try {
    // Config de runtime ANTES de qualquer dependência ser criada.
    const config = await loadAppConfig();
    document.title = config.appName;

    const kernel = createAppKernel(config);
    // Sessão em memória: recupera pelo cookie (refresh) antes de decidir as rotas. A landing
    // é pública e igual com ou sem sessão: desenha sem esperar, e as rotas protegidas
    // esperam dentro do router.
    const session = trackSession(kernel.initialize());
    const publicLandingPaths = [
      import.meta.env.BASE_URL,
      `${import.meta.env.BASE_URL}natal`,
      `${import.meta.env.BASE_URL}natal/`,
    ];
    if (!publicLandingPaths.includes(window.location.pathname)) await session.promise;
    const queryClient = createQueryClient(kernel);
    const router = createAppRouter(session);
    configureAnalytics(config.analytics?.ga4);
    // Contagem própria das páginas públicas, sem cookie; o operador não entra nos números.
    const trackVisit = createVisitTracker({
      apiBaseUrl: config.apiBaseUrl,
      ready: session.promise.catch(() => undefined),
      isOperator: () => kernel.auth.hasRole("platform"),
    });
    trackPage(router.state.location);
    trackVisit(router.state.location);
    let lastPath = router.state.location.pathname;
    router.subscribe((state) => {
      if (state.location.pathname === lastPath) return;
      lastPath = state.location.pathname;
      trackPage(state.location);
      trackVisit(state.location);
    });

    root.render(
      <StrictMode>
        <AppProviders kernel={kernel} queryClient={queryClient}>
          <RouterProvider router={router} />
        </AppProviders>
      </StrictMode>,
    );
  } catch (error) {
    console.error(error);
    root.render(
      <div style={{ padding: 24, fontFamily: "system-ui" }}>
        <h1>Não foi possível iniciar a aplicação.</h1>
        <pre style={{ whiteSpace: "pre-wrap" }}>{String(error)}</pre>
      </div>,
    );
  }
}

void bootstrap();
